import {PriorityConfig} from '@aboutyou/data-fetcher';
import {Grid, useScrollToTopOnUnload} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {StreamTileListProps} from '../../../features/ProductStream/StreamTileList';
import {TilesByPage} from '../../../features/ProductStream/TilesByPage';
import {StreamPaddingCSS} from '../../../features/ProductStream/helpers';
import {
  LoadMoreComponentDisplayConfig,
  ProductStreamTokenPagination,
} from '../../../features/ProductStreamPagination/ProductStreamTokenPagination';
import {useShallowRenderingState} from '../../../features/ProductTile/utils';
import {useTokenBasedStreamPages} from '../../../features/TokenBasedStreamPages/useTokenBasedStreamPages';
import {StoryProductStreamEmpty} from './StoryProductStreamEmpty';
import {StoryStreamTileList} from './StoryStreamTileList';
import {useIsInitialStoryStreamRequestEmpty} from './hooks/useIsInitialStoryStreamRequestEmpty';
import {useStoryPageStream} from './hooks/useStoryPageStream';
import {useStoryPageStreamNext} from './hooks/useStoryPageStreamNext';

const HISTORY_STATE_LAST_VISIBLE_ITEMS_KEY =
  'story_product_stream_last_visible_items';

interface StoryProductStreamProps extends StreamTileListProps, PriorityConfig {
  loadMoreComponentDisplayConfig: LoadMoreComponentDisplayConfig;
}

export const StoryProductStream: React.FC<StoryProductStreamProps> = ({
  renderingPriority,
  loadMoreComponentDisplayConfig,
  ...viewProps
}) => {
  useScrollToTopOnUnload();

  const {pages, isNextPageReady, mountNextPage, endOfStreamReached} =
    useTokenBasedStreamPages({
      uniqueKey: 'story',
      useRequest: useStoryPageStream,
      useNextRequest: useStoryPageStreamNext,
    });

  const shallowRenderingState = useShallowRenderingState({
    uniqueKey: HISTORY_STATE_LAST_VISIBLE_ITEMS_KEY,
  });

  const isInitialRequestEmpty = useIsInitialStoryStreamRequestEmpty({
    useRequest: useStoryPageStream,
  });

  return (
    <section>
      <StyledGrid
        columns={viewProps.gridColumns}
        enableEnhancedTileRendering
        uniqueKey="storyProductStream"
      >
        {pages.map(page => (
          <TilesByPage
            key={page.page}
            page={page}
            StreamTileList={StoryStreamTileList}
            renderingPriority={renderingPriority}
            shallowRenderingState={shallowRenderingState}
            {...viewProps}
          />
        ))}
      </StyledGrid>
      {isNextPageReady && !endOfStreamReached && (
        <ProductStreamTokenPagination
          mountNextPage={mountNextPage}
          loadMoreComponentDisplayConfig={loadMoreComponentDisplayConfig}
        />
      )}
      {isInitialRequestEmpty && <StoryProductStreamEmpty />}
    </section>
  );
};

const StyledGrid = styled(Grid)`
  ${StreamPaddingCSS}
`;
